import { Marker, Position } from '@/map/domain/entities/map.types';

/**
 * Interface for PositionChangedNotification from SignalR
 */
export interface PositionChangedNotification {
  id: number | string;
  latitude: number;
  longitude: number;
  dbName?: string;
  // Additional fields that might be present
  name?: string;
  gpsLocationType?: string;
  online?: boolean;
  moving?: boolean;
  [key: string]: unknown;
}

/**
 * Service to handle GPS position updates from SignalR
 * Manages marker position updates in real-time
 */
export class GpsPositionUpdateService {
  private markerUpdateCallbacks: Map<string | number, (position: Position) => void> = new Map();
  private allMarkersUpdateCallback: ((updates: Map<string | number, Position>) => void) | null = null;

  /**
   * Register a callback for a specific marker ID
   */
  onMarkerUpdate(id: string | number, callback: (position: Position) => void): () => void {
    this.markerUpdateCallbacks.set(id, callback);
    return () => {
      this.markerUpdateCallbacks.delete(id);
    };
  }

  /**
   * Register a callback for all marker updates
   */
  onAllMarkersUpdate(callback: (updates: Map<string | number, Position>) => void): () => void {
    this.allMarkersUpdateCallback = callback;
    return () => {
      this.allMarkersUpdateCallback = null;
    };
  }

  /**
   * Process a position changed notification
   * Filters by tenant (DbName) and updates marker positions
   */
  processPositionUpdate(notification: PositionChangedNotification, currentTenantDbName?: string): void {
    // Filter by tenant if DbName is provided in notification
    if (notification.dbName && currentTenantDbName && notification.dbName !== currentTenantDbName) {
      return; // Skip updates for other tenants
    }

    // Validate position data
    if (notification.id == null || notification.latitude == null || notification.longitude == null) {
      console.warn('Invalid position update notification:', notification);
      return;
    }

    const position: Position = {
      lat: Number(notification.latitude),
      lng: Number(notification.longitude)
    };

    // Update specific marker if callback is registered
    const markerCallback = this.markerUpdateCallbacks.get(notification.id);
    if (markerCallback) {
      try {
        markerCallback(position);
      } catch (error) {
        console.error(`Error updating marker ${notification.id}:`, error);
      }
    }

    // Update all markers callback
    if (this.allMarkersUpdateCallback) {
      const updates = new Map<string | number, Position>();
      updates.set(notification.id, position);
      try {
        this.allMarkersUpdateCallback(updates);
      } catch (error) {
        console.error('Error in all markers update callback:', error);
      }
    }
  }

  /**
   * Update marker position in a markers array
   * Returns a new array with updated positions
   */
  updateMarkerPosition(markers: Marker[], id: string | number, position: Position): Marker[] {
    return markers.map((marker) => {
      if (String(marker.id) === String(id)) {
        return {
          ...marker,
          position
        };
      }
      return marker;
    });
  }

  /**
   * Batch update multiple marker positions
   */
  batchUpdateMarkerPositions(markers: Marker[], updates: Map<string | number, Position>): Marker[] {
    const updatesMap = new Map(Array.from(updates.entries()).map(([id, pos]) => [String(id), pos]));

    return markers.map((marker) => {
      const update = updatesMap.get(String(marker.id));
      if (update) {
        return {
          ...marker,
          position: update
        };
      }
      return marker;
    });
  }
}
